import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';

const PANEL_WIDTH = 280;
const VIEWPORT_GUTTER = 12;

interface SignalInfoTooltipProps {
  content: string;
  label: string;
  children: ReactNode;
}

export const SignalInfoTooltip = ({ content, label, children }: SignalInfoTooltipProps) => {
  const triggerRef = useRef<HTMLSpanElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const showTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hoveringRef = useRef(false);
  const [open, setOpen] = useState(false);
  const [pinned, setPinned] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const prefersHoverRef = useRef(
    typeof window !== 'undefined' &&
      window.matchMedia('(hover: hover) and (pointer: fine)').matches
  );

  const clearTimers = () => {
    if (showTimerRef.current) {
      clearTimeout(showTimerRef.current);
      showTimerRef.current = null;
    }
    if (hideTimerRef.current) {
      clearTimeout(hideTimerRef.current);
      hideTimerRef.current = null;
    }
  };

  const updatePosition = useCallback(() => {
    const rect = triggerRef.current?.getBoundingClientRect();
    if (!rect) return;

    const panelWidth = Math.min(PANEL_WIDTH, window.innerWidth - VIEWPORT_GUTTER * 2);
    let left = rect.right - panelWidth;
    left = Math.max(VIEWPORT_GUTTER, Math.min(left, window.innerWidth - panelWidth - VIEWPORT_GUTTER));

    setPosition({
      top: rect.bottom + 8,
      left
    });
  }, []);

  const show = useCallback(() => {
    hoveringRef.current = true;
    clearTimers();
    showTimerRef.current = setTimeout(() => {
      if (!hoveringRef.current && !pinned) return;
      updatePosition();
      setOpen(true);
    }, 200);
  }, [pinned, updatePosition]);

  const hide = useCallback(() => {
    hoveringRef.current = false;
    if (pinned) return;
    clearTimers();
    hideTimerRef.current = setTimeout(() => {
      if (!hoveringRef.current && !pinned) {
        setOpen(false);
      }
    }, 120);
  }, [pinned]);

  const handleTriggerClick = () => {
    if (prefersHoverRef.current) return;

    clearTimers();
    setPinned((current) => {
      const next = !current;
      if (next) {
        updatePosition();
        setOpen(true);
      } else {
        setOpen(false);
      }
      return next;
    });
  };

  useEffect(() => {
    if (!open) return undefined;

    const reposition = () => updatePosition();
    window.addEventListener('scroll', reposition, true);
    window.addEventListener('resize', reposition);
    return () => {
      window.removeEventListener('scroll', reposition, true);
      window.removeEventListener('resize', reposition);
    };
  }, [open, updatePosition]);

  useEffect(() => {
    if (!pinned) return undefined;

    const handlePointerDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (triggerRef.current?.contains(target) || panelRef.current?.contains(target)) return;
      setPinned(false);
      setOpen(false);
    };

    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, [pinned]);

  useEffect(() => clearTimers, []);

  return (
    <>
      <TriggerWrap
        ref={triggerRef}
        onMouseEnter={show}
        onMouseLeave={hide}
        onMouseDown={(event) => {
          if (prefersHoverRef.current) event.preventDefault();
        }}
        onFocus={show}
        onBlur={hide}
        onClick={handleTriggerClick}
      >
        {children}
      </TriggerWrap>

      {open &&
        createPortal(
          <InfoPanel
            ref={panelRef}
            role="tooltip"
            style={{
              position: 'fixed',
              top: position.top,
              left: position.left,
              width: Math.min(PANEL_WIDTH, window.innerWidth - VIEWPORT_GUTTER * 2),
              zIndex: 10000
            }}
            onMouseEnter={show}
            onMouseLeave={hide}
          >
            <strong>{label}</strong>
            <span>{content}</span>
          </InfoPanel>,
          document.body
        )}
    </>
  );
};

const TriggerWrap = styled.span`
  display: inline-flex;
  align-items: center;
`;

const InfoPanel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 10px 12px;
  border-radius: 10px;
  background: ${({ theme }) => theme.signals.card_bg};
  border: 1px solid ${({ theme }) => theme.signals.card_border};
  box-shadow: 0 8px 24px rgba(58, 34, 32, 0.14);
  color: ${({ theme }) => theme.signals.text_secondary};
  font-size: 12px;
  line-height: 1.5;
  pointer-events: auto;
  animation: signalInfoFadeIn 0.15s ease-out;

  strong {
    color: ${({ theme }) => theme.signals.text_primary};
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.7px;
  }

  span {
    display: block;
    overflow-wrap: break-word;
  }

  @keyframes signalInfoFadeIn {
    from {
      opacity: 0;
      transform: translateY(-4px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;
